import EditProfileModal from '@/components/EditProfileModal';
import ChangeEmailModal from '@/components/ChangeEmailModal';
import { useAuth } from '@/hooks/useAuth';
import { getInitials } from '@/utils';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Profile page component displaying user information
 * @returns {JSX.Element} Profile component
 */
const Profile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  const handleEditSuccess = updatedUser => {
    updateUser(updatedUser);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
              <p className="mt-2 text-gray-600">
                Manage your account information and preferences
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Go Back
            </button>
          </div>
        </div>

        {/* Profile Card */}
        <div className="bg-white shadow rounded-lg">
          {/* Profile Header */}
          <div className="px-6 py-8 border-b border-gray-200">
            <div className="flex items-center space-x-6">
              {/* Avatar */}
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">
                    {getInitials(user.name || 'User')}
                  </span>
                </div>
              )}

              {/* User Info */}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">
                  {user.name || 'User'}
                </h2>
                <p className="text-lg text-gray-600">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Personal Information
                </h3>
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4 transition-colors hover:border-gray-300 bg-white">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      Full Name
                    </label>
                    <div className="flex items-center justify-between">
                      <p className="text-base text-gray-900 font-medium">
                        {user.name || 'Not provided'}
                      </p>
                      <button
                        type="button"
                        onClick={() => setIsModalOpen(true)}
                        className="text-sm px-3 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 rounded-md font-medium whitespace-nowrap ml-4 transition-colors"
                      >
                        Edit Name
                      </button>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4 transition-colors hover:border-gray-300 bg-white">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      Email Address
                    </label>
                    <div className="flex items-center justify-between">
                      <p className="text-base text-gray-900 font-medium truncate">{user.email}</p>
                      <button
                        type="button"
                        onClick={() => setIsChangeEmailModalOpen(true)}
                        className="text-sm px-3 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 rounded-md font-medium whitespace-nowrap ml-4 transition-colors"
                      >
                        Change Email
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Account Information
                </h3>
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4 transition-colors hover:border-gray-300 bg-white">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      Account Status
                    </label>
                    <div className="flex items-center mt-0.5">
                      <span className="relative flex h-3 w-3 mr-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                      <span className="text-base font-medium text-gray-900">Active</span>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4 transition-colors hover:border-gray-300 bg-white">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      Member Since
                    </label>
                    <p className="text-base text-gray-900 font-medium">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                        : 'Not available'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <EditProfileModal
        isOpen={isModalOpen}
        user={user}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleEditSuccess}
      />

      <ChangeEmailModal
        isOpen={isChangeEmailModalOpen}
        onClose={() => setIsChangeEmailModalOpen(false)}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default Profile;
